import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminPageHeader } from '../components/layout/AdminPageHeader';
import { Card, Badge, Button, Spinner } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { adminApi, routeApi } from '../services/api';
import CreateRouteModal from '../components/modals/CreateRouteModal';
import { toast } from 'sonner';

const AdminRoutes = () => {
  const { t } = useTranslation();
  const [routes, setRoutes] = useState([]);
  const [mahallas, setMahallas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [routesData, mahallasData] = await Promise.all([
        routeApi.getAll(),
        adminApi.getMahallas()
      ]);
      setRoutes(routesData || []);
      setMahallas(mahallasData || []);
    } catch (err) {
      toast.error(t('adminRoutes.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      await routeApi.activate(id);
      toast.success(t('adminRoutes.activated'));
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) return <div className="h-[60vh] flex items-center justify-center"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-10 pb-10">
      <AdminPageHeader
        tag="LOGISTICS"
        title={t('dashboard.routesManagement') || "Yo'nalishlar boshqaruvi"}
        subtitle={`${routes.length} ${t('dashboard.activeRoutes') || 'TOTAL ROUTES IN SYSTEM'}`}
        icon="alt_route"
      >
        <Button
          variant="emerald"
          onClick={() => setShowCreateModal(true)}
          className="h-14 px-8 rounded-3xl font-black uppercase italic text-xs tracking-widest shadow-xl shadow-emerald-500/20"
        >
          <span className="material-symbols-outlined mr-2">add_road</span>
          {t('dashboard.newRoute') || "Yangi yo'nalish"}
        </Button>
      </AdminPageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Routes List */}
        <div className="lg:col-span-1 space-y-6 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar">
          <AnimatePresence mode="popLayout">
            {routes.map((route, idx) => (
              <motion.div
                key={route.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="p-6 rounded-[30px] border-slate-100 dark:border-slate-800/50 hover:border-[#1e9f6b]/30 transition-all group relative overflow-hidden">
                   <div className="absolute top-0 right-0 p-4">
                      <Badge 
                        variant={route.status === 'ACTIVE' ? 'green' : route.status === 'COMPLETED' ? 'slate' : 'blue'} 
                        pulse={route.status === 'ACTIVE'}
                        className="rounded-xl italic uppercase text-[8px] font-black tracking-widest border-0"
                      >
                         {route.status}
                      </Badge>
                   </div>
                   <div className="flex items-center gap-4 mb-6">
                      <div className="h-14 w-14 rounded-2xl bg-[#1e9f6b]/10 flex items-center justify-center">
                         <span className="material-symbols-outlined text-[#1e9f6b] text-3xl">local_shipping</span>
                      </div>
                      <div>
                         <div className="text-xs font-black text-slate-400 uppercase tracking-widest">#{route.id.slice(0,6)}</div>
                         <div className="text-lg font-black italic uppercase text-slate-900 dark:text-white leading-none mt-1">
                            {route.vehicle?.model || 'Transport'}
                         </div>
                      </div>
                   </div>
                   
                   <div className="space-y-4">
                      <div className="flex items-center gap-3">
                         <div className="h-8 w-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center font-black text-[10px] text-slate-400 italic uppercase">
                            {route.driver?.name?.[0] || 'D'}
                         </div>
                         <div>
                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('adminRoutes.driver')}</div>
                            <div className="text-xs font-black italic uppercase text-slate-800 dark:text-slate-200">{route.driver?.name}</div>
                         </div>
                      </div>
                      <div className="flex items-center gap-3">
                         <div className="h-8 w-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center">
                            <span className="material-symbols-outlined text-slate-400 text-sm">map</span>
                         </div>
                         <div>
                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('adminRoutes.regions')}</div>
                            <div className="text-xs font-black italic uppercase text-slate-800 dark:text-slate-200 truncate max-w-[150px]">
                               {route.mahallas?.map(m => m.name).join(', ') || t('adminRoutes.unassigned')}
                            </div>
                         </div>
                      </div>
                   </div>

                   <div className="mt-6 pt-6 border-t border-slate-50 dark:border-slate-800/50 flex gap-2">
                      {route.status === 'CREATED' && (
                         <Button variant="emerald" size="sm" className="flex-1 rounded-xl text-[10px] font-black uppercase italic tracking-widest" onClick={() => handleActivate(route.id)}>{t('dashboard.activateRoute')}</Button>
                      )}
                      <Button variant="ghost" size="sm" className="flex-1 rounded-xl text-[10px] font-black uppercase italic tracking-widest">{t('common.details')}</Button>
                   </div>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Map View */}
        <div className="lg:col-span-2">
          <Card className="rounded-[40px] border-slate-100 dark:border-slate-800/50 overflow-hidden relative h-[700px] bg-slate-50/50 dark:bg-slate-900/30">
            <div className="p-8 border-b border-slate-100 dark:border-slate-800/50 flex justify-between items-center bg-white dark:bg-slate-900">
              <h3 className="text-xl font-black italic uppercase text-slate-900 dark:text-white">{t('adminRoutes.logisticsMap')}</h3>
              <div className="flex gap-2">
                  <Badge variant="green" className="rounded-xl font-black italic uppercase text-[8px] tracking-widest">LIVE TRACKING ACTIVE</Badge>
              </div>
            </div>

            <div className="relative h-full flex flex-col items-center justify-center p-10 text-center text-slate-400">
               <div className="h-24 w-24 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6">
                  <span className="material-symbols-outlined text-4xl">map</span>
               </div>
               <h4 className="text-lg font-black italic uppercase text-slate-900 dark:text-white mb-2">{t('adminRoutes.mapLoading')}</h4>
               <p className="max-w-md text-xs font-bold uppercase tracking-widest opacity-60">
                  {t('adminRoutes.mapDesc')}
               </p>
               
               {/* Simplified visualization of mahallas */}
               <div className="mt-10 grid grid-cols-4 gap-4 w-full">
                  {mahallas.slice(0, 12).map(m => (
                     <div key={m.id} className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-sm">
                        <div className="text-[10px] font-black text-[#1e9f6b] uppercase mb-1">MAHALLA</div>
                        <div className="text-xs font-black italic uppercase text-slate-800 dark:text-slate-200">{m.name}</div>
                     </div>
                  ))}
               </div>
            </div>
          </Card>
        </div>
      </div>

      <AnimatePresence>
        {showCreateModal && (
          <CreateRouteModal 
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchData();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminRoutes;
